import React, { useMemo } from 'react'
import { Alert, Input } from '@surecompanies/core_components'
import { keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'

import BenefitSelect, { BenefitOption } from './BenefitSelect'
import { typography } from './tokens'
import styles from './AdjustPaymentModal.module.scss'
import { CloseXIcon, TrashIcon } from './AdjustPaymentModal.icons'
import { fmtMoney, isMoneyInput } from './AdjustPaymentModal.helpers'
import { ChipState, GroupState } from './AdjustPaymentModal.types'
import {
  PeriodSuggestion,
  useSuggestPeriods,
} from '../../api/reconciliation-v2-api-client'

/**
 * The 12-chip month grid, shared by the allocation groups and the manual form.
 * Selection outranks the out-of-coverage signal, so every month has a selected
 * state: in-window selection is the brand teal, out-of-window selection is grey
 * (`selectedOut`) — picked, but still visibly a month nothing was owed for.
 * Each chip shows the month label plus the period's current allocation.
 */
export const MonthChipGrid: React.FC<{
  suggestions: PeriodSuggestion[]
  isSelected: (period: string) => boolean
  onToggle: (period: string) => void
  /** Read-only gate — disables every chip for users without write access. */
  disabled?: boolean
}> = ({ suggestions, isSelected, onToggle, disabled }) => (
  <div className={styles.chipGrid}>
    {/* Chronological (Jan → Dec) order — the backend returns them ranked by
        relevance, which reads as random in a month grid. periodStartDate is
        YYYY-MM-DD so a string compare is calendar order (and cross-year safe). */}
    {[...suggestions]
      .sort((a, b) => a.periodStartDate.localeCompare(b.periodStartDate))
      .map((sug) => {
        const period = sug.periodStartDate
        const outOfCoverage = sug.pastBenefitEnd || sug.preBenefitStart
        // Out-of-coverage months are ALLOCATABLE — an operator may deliberately
        // land money on a month the benefit was never in force for (a binder
        // payment before the effective date, a carrier billing after
        // termination). `out` is a signal, not a gate, so selection wins over it:
        // a selected chip must read as selected whichever side of the window it
        // sits on. Out of window it reads grey rather than teal, so the pick is
        // unmistakable without the chip claiming the month was ever billable.
        // See docs/adr/0004 in the enrollment-platform repo.
        const state: ChipState = isSelected(period)
          ? outOfCoverage
            ? 'selectedOut'
            : 'selected'
          : outOfCoverage
            ? 'out'
            : 'idle'
        // The `idle` state is the base class; every other state adds a modifier.
        const stateMod = state === 'idle' ? '' : ` ${styles[state]}`
        // Chip shows the benefit's CURRENT allocation for that month (full
        // decimals) — its actual funded state, not the flat expected repeated on
        // every month. An out-of-coverage month stays dashed until money lands
        // on it: the dash says "nothing was ever owed here", which stays true,
        // while an amount says "but money landed anyway".
        const amountFmt =
          outOfCoverage && sug.currentTotal === 0
            ? '—'
            : fmtMoney(sug.currentTotal)
        return (
          <button
            key={period}
            type="button"
            className={`${styles.monthChip}${stateMod}`}
            // The state as data, not only as a hashed CSS-module class: the
            // class names are mocked away under jest, so this is the only
            // handle a test has on which of the four appearances rendered.
            data-state={state}
            disabled={disabled}
            title={
              sug.pastBenefitEnd
                ? 'After benefit end — nothing was owed for this month'
                : sug.preBenefitStart
                  ? 'Before benefit start — nothing was owed for this month'
                  : undefined
            }
            onClick={() => onToggle(period)}
          >
            <span className={`${styles.chipLabel}${stateMod}`}>
              {dayjs(period).format('MMM')}
            </span>
            <span className={`${styles.chipAmount}${stateMod}`}>
              {amountFmt}
            </span>
          </button>
        )
      })}
  </div>
)

/**
 * The benefit's twelve months of ACTUAL allocation, read-only.
 *
 * Same data as {@link MonthChipGrid} — `currentTotal` off `useSuggestPeriods` —
 * deliberately, so the two cannot disagree on the same screen. The difference
 * is role: the chip grid is a PICKER inside the Allocate section, which means
 * reading the year today requires entering a flow you may not want. This one is
 * informational and always visible, so it is rendered as a strip of figures
 * rather than twelve buttons. Two month pickers in one modal, only one of which
 * does anything, would be worse than none.
 *
 * Renders nothing at all without a benefit. An unresolved transaction has no
 * year to show, and twelve dashes would read as "this benefit has no money"
 * rather than "we do not know the benefit".
 */
export const BenefitYearStrip: React.FC<{
  benefitId: string | null | undefined
  /**
   * The source amount. Used ONLY to satisfy `useSuggestPeriods`, whose `enabled`
   * guard requires one — the ranking it produces (`score`, `reasons`) is
   * discarded here. Passing the same amount the Allocate section passes is what
   * makes both share one request.
   */
  amount: number | null
  transactionDate?: string
}> = ({ benefitId, amount, transactionDate }) =>
  // Keyed on the benefit, which is what makes the `keepPreviousData` below
  // safe: a benefit CHANGE remounts and starts from nothing, so one benefit's
  // money can never sit on screen under another's name. Done here rather than
  // asked of callers — a forgotten key would be a silently wrong strip.
  benefitId ? (
    <YearStrip
      key={benefitId}
      benefitId={benefitId}
      amount={amount}
      transactionDate={transactionDate}
    />
  ) : null

const YearStrip: React.FC<{
  benefitId: string
  amount: number | null
  transactionDate?: string
}> = ({ benefitId, amount, transactionDate }) => {
  const { data: suggestions } = useSuggestPeriods(
    { benefitId, amount, transactionDate },
    // `amount` is in the query key but buys the strip NOTHING — it ranks
    // (`score`, `reasons`), and `currentTotal` is the same figure whatever it
    // is. So picking a different transaction in the cell changes the key, and
    // without this the strip would vanish and the modal reflow while a request
    // fetched numbers identical to the ones already on screen.
    { placeholderData: keepPreviousData }
  )
  if (!suggestions?.length) return null

  return (
    <div className={styles.yearStrip}>
      {[...suggestions]
        .sort((a, b) => a.periodStartDate.localeCompare(b.periodStartDate))
        .map((sug) => {
          const outOfCoverage = sug.pastBenefitEnd || sug.preBenefitStart
          return (
            <div
              key={sug.periodStartDate}
              className={`${styles.yearStripMonth}${
                outOfCoverage ? ` ${styles.yearStripOut}` : ''
              }`}
            >
              <span className={styles.yearStripLabel}>
                {dayjs(sug.periodStartDate).format('MMM').toUpperCase()}
              </span>
              <span className={styles.yearStripAmount}>
                {/* Dash only while the month is empty — once an operator has
                    deliberately allocated out of window, the strip must show
                    the money rather than deny it. */}
                {outOfCoverage && sug.currentTotal === 0
                  ? '—'
                  : fmtMoney(sug.currentTotal)}
              </span>
            </div>
          )
        })}
    </div>
  )
}

export interface AllocationGroupProps {
  group: GroupState
  sourceAmount: number
  transactionDate?: string
  year?: number
  currentBenefit: BenefitOption | null
  open: boolean
  onBenefitChange: (id: string, benefit: BenefitOption) => void
  onToggleMonth: (id: string, period: string) => void
  onRowAmount: (id: string, period: string, amount: string) => void
  onRemoveRow: (id: string, period: string) => void
  onSplitEvenly: (id: string) => void
  onRemoveGroup: (id: string) => void
  divided: boolean
  /** Benefit IDs already picked by OTHER groups — hidden from this picker. */
  excludeBenefitIds: ReadonlySet<string>
  /** Write gate — disables the group's Remove action for read-only users. */
  canWrite: boolean
}

/**
 * One benefit's allocation editor. Owns its own `useSuggestPeriods` (each group
 * targets a different benefit, so the hook can't be lifted into the parent
 * loop) and its benefit-picker; all mutable allocation state lives in the
 * parent and flows back through callbacks.
 */
export const AllocationGroup: React.FC<AllocationGroupProps> = ({
  group,
  sourceAmount,
  transactionDate,
  year,
  currentBenefit,
  open,
  onBenefitChange,
  onToggleMonth,
  onRowAmount,
  onRemoveRow,
  onSplitEvenly,
  onRemoveGroup,
  divided,
  excludeBenefitIds,
  canWrite,
}) => {
  const benefitId = group.benefit?.benefitId

  const { data: suggestions, isError: isSuggestError } = useSuggestPeriods(
    {
      benefitId,
      amount: sourceAmount || null,
      transactionDate,
    },
    { enabled: open && !!benefitId && sourceAmount > 0 }
  )

  const rowPeriods = useMemo(() => Object.keys(group.rows).sort(), [group.rows])
  const hasRows = rowPeriods.length > 0

  const expectedByPeriod = useMemo(() => {
    const map = new Map<string, number | null>()
    ;(suggestions ?? []).forEach((s) =>
      map.set(s.periodStartDate, s.expectedAmount)
    )
    return map
  }, [suggestions])

  return (
    <div
      className={`${styles.groupWrap}${divided ? ` ${styles.divided}` : ''}`}
    >
      <div>
        <div className={styles.fieldLabel}>Select benefit</div>
        <BenefitSelect
          value={group.benefit}
          onChange={(b) => onBenefitChange(group.id, b)}
          year={year}
          currentBenefit={currentBenefit}
          placeholder="Search by name or benefit ID…"
          excludeBenefitIds={excludeBenefitIds}
          disabled={!canWrite}
        />
      </div>

      {benefitId ? (
        <div>
          <div className={styles.fieldLabel}>Select month</div>
          {isSuggestError ? (
            <Alert
              type="error"
              message="Could not load period suggestions"
              description="Failed to fetch available months. Please try closing and reopening."
              style={{ marginTop: 4 }}
            />
          ) : (
            <MonthChipGrid
              suggestions={suggestions ?? []}
              isSelected={(period) => period in group.rows}
              onToggle={(period) => onToggleMonth(group.id, period)}
              disabled={!canWrite}
            />
          )}
        </div>
      ) : null}

      {hasRows ? (
        <div className={styles.monthRows}>
          {rowPeriods.map((period) => {
            const expected = expectedByPeriod.get(period)
            return (
              <div className={styles.monthRow} key={period}>
                <span className={styles.monthRowLabel}>
                  {dayjs(period).format('MMMM YYYY')}
                </span>
                <span className={styles.monthRowExpected}>
                  Expected{' '}
                  <span className={styles.monthRowExpectedAmt}>
                    {expected != null ? fmtMoney(expected) : '—'}
                  </span>
                </span>
                <div className={styles.amountInputWrap}>
                  <Input
                    prefix="$"
                    placeholder="0.00"
                    inputMode="decimal"
                    value={group.rows[period]}
                    disabled={!canWrite}
                    style={{ ...typography.mono }}
                    onChange={(e) => {
                      const raw = e.target.value
                      if (isMoneyInput(raw)) {
                        onRowAmount(group.id, period, raw)
                      }
                    }}
                  />
                </div>
                <button
                  className={styles.removeRowBtn}
                  type="button"
                  aria-label="Remove month"
                  disabled={!canWrite}
                  onClick={() => onRemoveRow(group.id, period)}
                >
                  <CloseXIcon />
                </button>
              </div>
            )
          })}
        </div>
      ) : null}

      <div
        className={`${styles.groupFooterRow}${
          hasRows ? ` ${styles.hasRows}` : ''
        }`}
      >
        {hasRows ? (
          <button
            className={styles.splitEvenlyBtn}
            type="button"
            disabled={!canWrite}
            onClick={() => onSplitEvenly(group.id)}
          >
            Split evenly
          </button>
        ) : null}
        <button
          className={styles.removeGroupBtn}
          type="button"
          disabled={!canWrite}
          onClick={() => onRemoveGroup(group.id)}
        >
          Remove
          <TrashIcon />
        </button>
      </div>
    </div>
  )
}
